import type { ImportSource } from '../index';
import type { ImportedItem } from './types';
import { parseCsv } from './csv';

/**
 * Parse a browser password-manager CSV export.
 *
 * Chrome and Firefox both export a flat, quoted CSV whose credential columns
 * are named identically (`url`, `username`, `password`), so one parser serves
 * both. Columns are looked up **by header name**, never by position, which is
 * what makes that possible: the two browsers order and pad their headers
 * differently and only overlap on the three fields that matter.
 *
 *   Chrome:  name,url,username,password,note
 *   Firefox: url,username,password,httpRealm,formActionOrigin,guid,
 *            timeCreated,timeLastUsed,timePasswordChanged
 *
 * Unknown columns are ignored and missing ones are simply absent from the
 * result — so Firefox's bookkeeping fields drop out, and because Firefox has no
 * `name` column its items fall back to being named by their URL.
 *
 * Neither browser exports TOTP, folder, or favorite.
 *
 * `source` is stamped onto every item as `importSource` so the autofill
 * suggestion list can tell a Chrome-imported duplicate from a Firefox-imported
 * one — the reason these are separate adapters rather than one shared tile.
 */
export function parseBrowserCsv(csv: string, source: ImportSource): ImportedItem[] {
  const rows = parseCsv(csv);
  if (rows.length < 2) return [];

  const headers = rows[0].map(h => h.trim().toLowerCase());
  const col = (name: string) => headers.indexOf(name);

  const iName     = col('name');
  const iUrl      = col('url');
  const iUsername = col('username');
  const iPassword = col('password');
  const iNote     = col('note');

  return rows.slice(1).map(cells => {
    const get = (i: number) => (i >= 0 ? (cells[i] ?? '').trim() : '');

    const item: ImportedItem = {
      type: 'login',
      name: get(iName) || get(iUrl) || 'Untitled',
      sensitive: false,
      favorite: false,
      tags: [],
      importSource: source,
    };

    const url      = get(iUrl);
    const username = get(iUsername);
    const password = get(iPassword);
    const note     = get(iNote);

    if (url)      item.url      = url;
    if (username) item.username = username;
    if (password) item.password = password;
    if (note)     item.content  = note;

    return item;
  });
}
